<template>
  <div class="exchange-bill-info">
    <KTCodePreview v-bind:title="''">
      <template v-slot:preview>
        <!-- Title row + actions -->
        <div
          class="d-flex align-items-start justify-content-between flex-wrap mb-4"
        >
          <div class="d-flex align-items-center">
            <span class="title-icon mr-3">
              <i class="fas fa-exchange-alt"></i>
            </span>
            <div>
              <h4 class="mb-0 font-weight-bolder">Xuất đổi sản phẩm</h4>
              <span class="text-muted">{{ pageSubtitle }}</span>
            </div>
          </div>
          <div class="d-flex align-items-center">
            <b-badge variant="light" class="mode-badge mr-3">
              <span class="status-dot"></span> EXCHANGE · mode = 3
            </b-badge>
          </div>
        </div>

        <b-skeleton-wrapper :loading="!renderComponent">
          <template #loading>
            <b-skeleton-table
              :rows="4"
              :columns="5"
              :table-props="{ bordered: true, striped: true }"
            ></b-skeleton-table>
          </template>

          <!-- Summary cards -->
          <b-row class="summary-row">
            <b-col md="3" sm="6" class="mb-3">
              <div class="summary-card">
                <div class="summary-icon"><i class="far fa-file-alt"></i></div>
                <div class="summary-content">
                  <span class="summary-label">HÓA ĐƠN GỐC</span>
                  <div class="summary-value">
                    #{{ mainModel.relatedBillNumber }}
                    <b-badge variant="light" class="ml-1">{{
                      billTypeName
                    }}</b-badge>
                  </div>
                  <span class="summary-sub">{{ mainModel.billCreatedAt }}</span>
                </div>
              </div>
            </b-col>
            <b-col md="3" sm="6" class="mb-3">
              <div class="summary-card">
                <div class="summary-icon"><i class="fas fa-store"></i></div>
                <div class="summary-content">
                  <span class="summary-label">KHO BÁN GỐC</span>
                  <div class="summary-value">{{ mainModel.storeName }}</div>
                  <span class="summary-sub">Nơi hàng cũ sẽ nhập lại</span>
                </div>
              </div>
            </b-col>
            <b-col md="3" sm="6" class="mb-3">
              <div class="summary-card">
                <div class="summary-icon"><i class="far fa-user"></i></div>
                <div class="summary-content">
                  <span class="summary-label">KHÁCH HÀNG</span>
                  <div class="summary-value">
                    {{ mainModel.customerName || '—' }}
                  </div>
                  <span class="summary-sub">{{
                    mainModel.customerMobile
                  }}</span>
                </div>
              </div>
            </b-col>
            <b-col md="3" sm="6" class="mb-3">
              <div class="summary-card">
                <div class="summary-icon"><i class="far fa-clock"></i></div>
                <div class="summary-content">
                  <span class="summary-label">THANH TOÁN</span>
                  <div class="summary-value">{{ paymentCardTitle }}</div>
                  <span class="summary-sub">{{ paymentCardSub }}</span>
                </div>
              </div>
            </b-col>
          </b-row>

          <!-- Reason + Stock flow -->
          <b-row>
            <b-col lg="7" class="mb-3">
              <div class="block-card h-100">
                <div class="block-head">
                  <i class="fas fa-exclamation-circle text-warning mr-2"></i>
                  <div>
                    <span class="block-title">Lý do xuất đổi *</span>
                    <div class="text-muted small">
                      Bắt buộc — quyết định nghiệp vụ áp dụng
                    </div>
                  </div>
                </div>
                <b-row class="mt-3">
                  <b-col
                    md="4"
                    sm="4"
                    class="mb-2"
                    v-for="reason in reasonTiles"
                    :key="reason.id"
                  >
                    <div
                      class="reason-tile"
                      :class="{ active: mainModel.reasonId === reason.id }"
                      @click="mainModel.reasonId = reason.id"
                    >
                      <i :class="reason.icon"></i>
                      <span class="reason-name">{{ reason.name }}</span>
                      <span class="reason-desc">{{ reason.desc }}</span>
                    </div>
                  </b-col>
                </b-row>
              </div>
            </b-col>
            <b-col lg="5" class="mb-3">
              <div class="block-card h-100">
                <div class="block-head">
                  <i class="fas fa-warehouse text-primary mr-2"></i>
                  <div>
                    <span class="block-title">Luồng kho</span>
                    <div class="text-muted small">
                      Kho nhập hàng cũ và kho xuất hàng mới
                    </div>
                  </div>
                </div>
                <b-form-group class="mt-3 mb-2" label="Kho nhập hàng cũ">
                  <b-form-input
                    size="sm"
                    :value="mainModel.storeName"
                    disabled
                  ></b-form-input>
                </b-form-group>
                <div class="text-center text-muted mb-2">
                  <i class="fas fa-arrow-down"></i>
                </div>
                <b-form-group class="mb-1" label="Kho xuất hàng mới">
                  <treeselect
                    ref="new-store-option"
                    :options="optionNewStores"
                    :multiple="false"
                    placeholder="Nhập cửa hàng"
                    retryText="Thử lại..."
                    retryTitle="Nhấp thử lại"
                    loadingText="Đang tải..."
                    noResultsText="Không có kết quả"
                    :match-keys="['label', 'shortName']"
                    v-model="mainModel.newStoreId"
                    @select="onSelectNewStore"
                  />
                </b-form-group>
                <span class="text-muted small">
                  <i class="fas fa-info-circle"></i> Hàng mới sẽ được xuất từ kho
                  (có thể khác kho gốc).
                </span>
              </div>
            </b-col>
          </b-row>

          <b-alert
            v-if="refundPendingAmount > 0 && !isStaffReason"
            show
            variant="warning"
            class="mb-3"
          >
            <i class="fas fa-info-circle mr-1"></i>
            Chênh lệch hoàn tiền
            <strong>{{ convertPrice(refundPendingAmount) }}</strong>
            sẽ xử lý ở phase sau. Bạn vẫn có thể lưu hóa đơn xuất đổi.
          </b-alert>

          <!-- Product pairs -->
          <div class="block-card mb-3">
            <BillExchangeProduct
              :rows="mainModel.billItems"
              :newStoreId="mainModel.newStoreId"
              :billType="mainModel.billType"
              :originalItems="originalItems"
              :lockPrices="isStaffReason"
              v-on:remove="onRemoveRow"
              v-on:add="onAddRow"
            />
          </div>

          <!-- Payment -->
          <b-row v-if="showPaymentSection">
            <b-col md="5" class="mb-3">
              <div class="block-card h-100">
                <BillExchangeCheckoutForm
                  :giveValue="totalGiveValue"
                  :exchangeCredit="exchangeCredit"
                  :amountDue="amountDue"
                  :refundPendingAmount="refundPendingAmount"
                  :requiresPayment="requiresPayment"
                  :paymentModel="paymentModel"
                  :onSubmiting="onSubmiting"
                  v-on:update-payment-info="onUpdatePaymentInfo"
                  v-on:update-payment-rows="onUpdatePaymentRows"
                />
              </div>
            </b-col>
          </b-row>

          <!-- Customer + note (was after products) -->
          <b-row>
            <b-col md="7" class="mb-3">
              <div class="block-card h-100">
                <div class="block-head">
                  <i class="far fa-user text-primary mr-2"></i>
                  <div>
                    <span class="block-title">Thông tin khách</span>
                    <div class="text-muted small">
                      Tự lấy từ hóa đơn gốc, có thể sửa
                    </div>
                  </div>
                </div>
                <b-row class="mt-3">
                  <b-col md="6">
                    <b-form-group label="Khách hàng">
                      <b-form-input
                        size="sm"
                        v-model="mainModel.customerName"
                        placeholder="Tên khách hàng"
                      ></b-form-input>
                    </b-form-group>
                  </b-col>
                  <b-col md="6">
                    <b-form-group label="Số điện thoại">
                      <b-form-input
                        size="sm"
                        v-model="mainModel.customerMobile"
                        placeholder="Số điện thoại"
                      ></b-form-input>
                    </b-form-group>
                  </b-col>
                </b-row>
              </div>
            </b-col>
            <b-col md="5" class="mb-3">
              <div class="block-card h-100">
                <div class="block-head">
                  <i class="far fa-sticky-note text-primary mr-2"></i>
                  <div>
                    <span class="block-title">Ghi chú</span>
                    <div class="text-muted small">Tùy chọn</div>
                  </div>
                </div>
                <b-form-textarea
                  class="mt-3"
                  size="sm"
                  v-model="mainModel.customerNote"
                  placeholder="VD: Khách đổi do lỗi màn hình..."
                  :rows="3"
                  :max-rows="4"
                ></b-form-textarea>
              </div>
            </b-col>
          </b-row>

          <!-- Sticky footer -->
          <div class="exchange-footer">
            <div class="footer-stats">
              <div class="stat">
                <span class="stat-label">CẶP ĐỔI</span>
                <span class="stat-value">{{ pairCount }}</span>
              </div>
              <div class="stat">
                <span class="stat-label">GIÁ TRỊ THU VỀ</span>
                <span class="stat-value text-success">{{
                  convertPrice(totalReturnValue)
                }}</span>
              </div>
              <div class="stat">
                <span class="stat-label">GIÁ TRỊ GIAO KHÁCH</span>
                <span class="stat-value">{{
                  convertPrice(totalGiveValue)
                }}</span>
              </div>
              <div class="stat">
                <span class="stat-label">CREDIT HĐ GỐC</span>
                <span class="stat-value text-success">{{
                  convertPrice(exchangeCredit)
                }}</span>
              </div>
              <div class="stat">
                <span class="stat-label">KHÁCH CẦN TRẢ</span>
                <span class="stat-value text-primary">{{
                  convertPrice(amountDue)
                }}</span>
              </div>
            </div>
            <div class="footer-actions">
              <span v-if="requiresPayment && paymentRemain > 0" class="text-danger mr-3 small">
                <i class="fas fa-exclamation-circle"></i>
                Còn thiếu {{ convertPrice(paymentRemain) }}
              </span>
              <span v-if="invalidCount" class="text-warning mr-3 small">
                <i class="fas fa-exclamation-triangle"></i>
                {{ invalidCount }} dòng chưa hợp lệ
              </span>
              <b-button
                variant="outline-secondary"
                size="sm"
                class="mr-2"
                @click="onCancel"
              >
                <i class="fas fa-times mr-1"></i> Hủy
              </b-button>
              <b-button
                variant="primary"
                size="sm"
                :disabled="onSubmiting || !canSubmit"
                @click="onSubmit"
              >
                <i class="fas fa-save mr-1"></i> Lưu hóa đơn đổi
              </b-button>
            </div>
          </div>
        </b-skeleton-wrapper>
      </template>
    </KTCodePreview>
  </div>
</template>

<script>
import KTCodePreview from '@/view/content/CodePreview.vue';
import ApiService from '@/core/services/api.service';
import moment from 'moment';
import { v4 as uuidv4 } from 'uuid';
import Treeselect from '@riophae/vue-treeselect';
import '@riophae/vue-treeselect/dist/vue-treeselect.css';
import apiCommonMixin from '@/view/mixins/api-common.mixins.js';
import { SET_BREADCRUMB } from '@/core/services/store/modules/breadcrumbs.module';
import { TIME_TRIGGER } from '@/utils/constants';
import { ITEM_TYPE, PRODUCT_TYPE, EXCHANGE_REASON, PAYMENT_TYPE } from '@/utils/enum';
import { EXCHANGE_REASON_NAME, BILL_TYPE_NAME } from '@/utils/enum-name';
import {
  makeToastFaile,
  makeToastSuccess,
  unMaskPrice,
  convertPrice,
  cloneDeep,
} from '@/utils/common';
import BillExchangeProduct from '@/view/components/bills/BillExchangeProduct.vue';
import BillExchangeCheckoutForm from '@/view/components/bills/BillExchangeCheckoutForm.vue';
import _sumBy from 'lodash/sumBy';

export default {
  mixins: [apiCommonMixin],
  components: {
    KTCodePreview,
    BillExchangeProduct,
    BillExchangeCheckoutForm,
    Treeselect,
  },
  data() {
    return {
      renderComponent: false,
      onSubmiting: false,
      reasonTiles: [
        {
          id: EXCHANGE_REASON.WRONG_PRODUCT_CODE,
          name: EXCHANGE_REASON_NAME[EXCHANGE_REASON.WRONG_PRODUCT_CODE],
          desc: 'NV chọn sai SP',
          icon: 'fas fa-barcode',
        },
        {
          id: EXCHANGE_REASON.WRONG_IMEI,
          name: EXCHANGE_REASON_NAME[EXCHANGE_REASON.WRONG_IMEI],
          desc: 'NV xuất nhầm IMEI',
          icon: 'fas fa-hashtag',
        },
        {
          id: EXCHANGE_REASON.CUSTOMER_EXCHANGE,
          name: EXCHANGE_REASON_NAME[EXCHANGE_REASON.CUSTOMER_EXCHANGE],
          desc: 'Khách yêu cầu đổi',
          icon: 'fas fa-sync-alt',
        },
      ],
      optionNewStores: [],
      allowedStoreIds: [],
      defaultNewStoreId: null,
      originalItems: [],
      saleBillModel: {
        totalAmount: 0,
        cashAmount: 0,
        transferAmount: 0,
        creditAmount: 0,
        installedMoneyAmount: 0,
        depositAmount: 0,
        transferFromOrder: 0,
        moneyCredit: 0,
        moneyInstallment: 0,
        pointAmount: 0,
      },
      paymentModel: {
        storeId: null,
        cash: [],
        transfer: [],
        installments: [],
        credits: [],
        refunds: [],
        deposit: [],
        paymentInfo: {
          cashAmount: 0,
          cashAccountId: null,
          cashAccountCode: '',
          transferAmount: 0,
          transferAccountantId: null,
          transferAccountCode: '',
          transferReferenceCode: '',
        },
      },
      mainModel: {
        relatedBillNumber: null,
        storeId: null,
        storeName: '',
        newStoreId: null,
        newStoreName: '',
        reasonId: null,
        billType: null,
        billCreatedAt: '',
        saleId: null,
        cashierId: null,
        customerName: '',
        customerMobile: '',
        customerAddress: '',
        customerNote: '',
        saleNote: '',
        billItems: [],
      },
    };
  },
  computed: {
    billTypeName() {
      return BILL_TYPE_NAME[this.mainModel.billType] || '';
    },
    pairCount() {
      return this.mainModel.billItems.length;
    },
    totalReturnValue() {
      return this.mainModel.billItems.reduce((sum, row) => {
        const price = Number(unMaskPrice(row.productPrice)) || 0;
        const qty = Number(row.oldQuantity) || 0;
        return sum + price * qty;
      }, 0);
    },
    totalGiveValue() {
      return this.mainModel.billItems.reduce((sum, row) => {
        const price = Number(unMaskPrice(row.newProductPrice)) || 0;
        const qty = this.isImeiRow(row)
          ? row.newImeiCode
            ? 1
            : 0
          : Number(row.newQuantity) || 0;
        return sum + price * qty;
      }, 0);
    },
    diffValue() {
      return this.totalGiveValue - this.totalReturnValue;
    },
    isStaffReason() {
      return [
        EXCHANGE_REASON.WRONG_PRODUCT_CODE,
        EXCHANGE_REASON.WRONG_IMEI,
      ].includes(this.mainModel.reasonId);
    },
    totalCustomerPaid() {
      const s = this.saleBillModel;
      return (
        (Number(s.cashAmount) || 0) +
        (Number(s.transferAmount) || 0) +
        (Number(s.creditAmount) || 0) +
        (Number(s.installedMoneyAmount) || 0) +
        (Number(s.depositAmount) || 0) +
        (Number(s.transferFromOrder) || 0) +
        (Number(s.moneyCredit) || 0) +
        (Number(s.moneyInstallment) || 0) +
        (Number(s.pointAmount) || 0)
      );
    },
    exchangeCredit() {
      const returnLineValue = this.totalReturnValue;
      const billTotal = Number(this.saleBillModel.totalAmount) || 0;
      const customerPaid = this.totalCustomerPaid;
      if (billTotal <= 0) {
        return Math.round(returnLineValue);
      }
      const proportional = (returnLineValue / billTotal) * customerPaid;
      return Math.round(Math.min(returnLineValue, proportional));
    },
    amountDue() {
      return Math.max(0, Math.round(this.totalGiveValue) - this.exchangeCredit);
    },
    refundPendingAmount() {
      return Math.max(
        0,
        this.exchangeCredit - Math.round(this.totalGiveValue),
      );
    },
    requiresPayment() {
      return (
        this.mainModel.reasonId === EXCHANGE_REASON.CUSTOMER_EXCHANGE &&
        this.amountDue > 0
      );
    },
    showPaymentSection() {
      return (
        this.mainModel.reasonId === EXCHANGE_REASON.CUSTOMER_EXCHANGE &&
        (this.requiresPayment || this.refundPendingAmount > 0)
      );
    },
    totalPaid() {
      const payments = [
        ...(this.paymentModel.cash || []),
        ...(this.paymentModel.transfer || []),
      ];
      return _sumBy(payments, (item) => Number(item.paymentAmount) || 0);
    },
    paymentRemain() {
      if (!this.requiresPayment) {
        return 0;
      }
      return Math.max(0, this.amountDue - this.totalPaid);
    },
    pageSubtitle() {
      return 'Tạo hóa đơn xuất đổi từ hóa đơn bán hàng gốc · thu chênh lệch trên HĐ mới';
    },
    paymentCardTitle() {
      if (this.isStaffReason) {
        return 'Không phát sinh (lỗi NV)';
      }
      if (this.requiresPayment) {
        return `Thu thêm ${convertPrice(this.amountDue)}`;
      }
      if (this.refundPendingAmount > 0) {
        return 'Chưa hoàn chênh lệch';
      }
      return 'Không cần thu thêm';
    },
    paymentCardSub() {
      if (this.requiresPayment) {
        return 'Credit HĐ gốc đã trừ vào tổng';
      }
      if (this.refundPendingAmount > 0) {
        return 'Hoàn tiền xử lý phase sau';
      }
      return 'Giá đối chiếu / credit HĐ gốc';
    },
    invalidCount() {
      return this.mainModel.billItems.filter((row) => !this.isRowValid(row))
        .length;
    },
    canSubmit() {
      return (
        !!this.mainModel.reasonId &&
        !!this.mainModel.newStoreId &&
        this.isStoreAllowed(this.mainModel.newStoreId) &&
        this.mainModel.billItems.length > 0 &&
        this.invalidCount === 0 &&
        (!this.requiresPayment || this.paymentRemain === 0)
      );
    },
  },
  watch: {
    'mainModel.reasonId'(reasonId) {
      if (
        reasonId === EXCHANGE_REASON.WRONG_PRODUCT_CODE ||
        reasonId === EXCHANGE_REASON.WRONG_IMEI
      ) {
        const originalMap = new Map(
          this.originalItems.map((item) => [
            `${item.productId}__${item.imeiCode || ''}`,
            item,
          ]),
        );
        this.mainModel.billItems.forEach((row) => {
          const key = `${row.productId}__${row.imeiCode || ''}`;
          const original = originalMap.get(key);
          if (original) {
            row.productPrice = original.productPrice || 0;
            row.newProductPrice = original.productPrice || 0;
          }
        });
        this.resetPaymentModel();
      }
    },
  },
  mounted() {
    this.$store.dispatch(SET_BREADCRUMB, [
      { title: 'Hóa đơn', route: '/bills' },
      { title: 'Danh sách hóa đơn', route: '/bills' },
      { title: 'Xuất đổi sản phẩm' },
    ]);
    this.mainModel.relatedBillNumber =
      this.$route.query.relatedBillNumber || null;
    this.bootstrapPage();
  },
  methods: {
    convertPrice,
    formatBillDate(value) {
      if (!value) {
        return '';
      }
      const parsed = moment(value);
      return parsed.isValid()
        ? parsed.format('DD/MM/YYYY HH:mm')
        : moment(String(value), moment.ISO_8601, true).isValid()
          ? moment(String(value)).format('DD/MM/YYYY HH:mm')
          : '';
    },
    resetPaymentModel() {
      this.paymentModel.cash = [];
      this.paymentModel.transfer = [];
      this.paymentModel.installments = [];
      this.paymentModel.credits = [];
      this.paymentModel.refunds = [];
      this.paymentModel.deposit = [];
      this.paymentModel.paymentInfo = {
        cashAmount: 0,
        cashAccountId: null,
        cashAccountCode: '',
        transferAmount: 0,
        transferAccountantId: null,
        transferAccountCode: '',
        transferReferenceCode: '',
      };
    },
    onUpdatePaymentInfo(paymentInfo) {
      this.paymentModel.paymentInfo = cloneDeep(paymentInfo);
    },
    onUpdatePaymentRows({ items, type }) {
      const payments = items.map((item) => ({
        ...item,
        paymentAmount: item.paymentAmount,
      }));
      switch (type) {
        case PAYMENT_TYPE.CASH:
          this.paymentModel.cash = payments;
          break;
        case PAYMENT_TYPE.TRANSFER:
          this.paymentModel.transfer = payments;
          break;
        default:
          break;
      }
    },
    calcPaymentsAmount(items = []) {
      return _sumBy(items, (item) => Number(item.paymentAmount) || 0);
    },
    isImeiRow(row) {
      return row.productType === PRODUCT_TYPE.PRODUCT_IMEI;
    },
    isRowValid(row) {
      if (!row.productId || !row.newProductId) {
        return false;
      }
      if (this.isImeiRow(row)) {
        return !!(row.newImeiCode && row.newImeiCode.trim());
      }
      return !!row.newQuantity && row.newQuantity >= 1;
    },
    async bootstrapPage() {
      await this.initNewStoreOptions();
      if (this.mainModel.relatedBillNumber) {
        this.getSaleBill();
      } else {
        makeToastFaile('Thiếu số hóa đơn bán hàng gốc!');
        this.renderComponent = true;
      }
    },
    async initNewStoreOptions() {
      const { stores, currentStoreId } = await this.fetchStoreByUser();
      this.allowedStoreIds = (stores || []).map((store) => store.id);
      this.optionNewStores = this.mapDataTreeSelect(stores, [
        'id',
        'name',
        'shortName',
      ]);
      this.defaultNewStoreId = currentStoreId;
      if (!this.allowedStoreIds.length) {
        makeToastFaile('Bạn không có quyền truy cập cửa hàng nào!');
      }
    },
    mapDataTreeSelect(items = [], keys = []) {
      return items.map((element) => ({
        id: element[keys[0]],
        label: element[keys[1]],
        shortName: element[keys[2]],
      }));
    },
    resolveNewStoreId(preferredStoreId) {
      const allowed = this.allowedStoreIds || [];
      if (!allowed.length) {
        this.mainModel.newStoreId = null;
        this.mainModel.newStoreName = '';
        return;
      }
      let storeId = null;
      if (preferredStoreId && allowed.includes(preferredStoreId)) {
        storeId = preferredStoreId;
      } else if (
        this.defaultNewStoreId &&
        allowed.includes(this.defaultNewStoreId)
      ) {
        storeId = this.defaultNewStoreId;
      } else {
        storeId = allowed[0];
      }
      this.mainModel.newStoreId = storeId;
      this.syncNewStoreName();
    },
    syncNewStoreName() {
      const store = (this.optionNewStores || []).find(
        (item) => item.id === this.mainModel.newStoreId,
      );
      this.mainModel.newStoreName = store ? store.label : '';
    },
    onSelectNewStore(node) {
      if (node?.id && node.id !== this.mainModel.newStoreId) {
        this.mainModel.newStoreId = node.id;
        this.mainModel.newStoreName = node.label;
        this.clearNewProductSelections();
      } else if (!node?.id) {
        this.mainModel.newStoreId = null;
        this.mainModel.newStoreName = '';
      }
    },
    clearNewProductSelections() {
      this.mainModel.billItems.forEach((row) => {
        row.newProductId = null;
        row.newProductName = '';
        row.newProductCode = '';
        row.newBarCode = '';
        row.newImageUrl = '';
        row.newProductType = null;
        row.newSearchText = '';
        row.newImeiCode = '';
        row.newQuantity = row.oldQuantity || 1;
      });
    },
    isStoreAllowed(storeId) {
      return this.allowedStoreIds.includes(storeId);
    },
    onCancel() {
      this.$router.push({ name: 'list-bill' });
    },
    getSaleBill() {
      ApiService.get(`v1/bills/${this.mainModel.relatedBillNumber}`)
        .then((response) => {
          const dataRes = response.data.data;
          this.mainModel.storeId = dataRes.storeId;
          this.mainModel.storeName = dataRes.storeName;
          this.resolveNewStoreId(dataRes.storeId);
          this.mainModel.billType = dataRes.type;
          this.mainModel.billCreatedAt = this.formatBillDate(dataRes.createdAt);
          this.mainModel.saleId = dataRes.saleId || null;
          this.mainModel.cashierId = dataRes.cashierId || null;
          this.mainModel.customerName = dataRes.customerName || '';
          this.mainModel.customerMobile = dataRes.customerPhone || '';
          this.mainModel.customerAddress = dataRes.customerAddress || '';

          this.saleBillModel.totalAmount = dataRes.totalAmount || 0;
          this.saleBillModel.cashAmount = dataRes.cashAmount || 0;
          this.saleBillModel.transferAmount = dataRes.transferAmount || 0;
          this.saleBillModel.creditAmount = dataRes.creditAmount || 0;
          this.saleBillModel.installedMoneyAmount =
            dataRes.installedMoneyAmount || 0;
          this.saleBillModel.pointAmount = dataRes.pointToMoney || dataRes.pointAmount || 0;
          this.saleBillModel.depositAmount = dataRes.depositAmount || 0;
          this.saleBillModel.transferFromOrder = dataRes.transferFromOrder || 0;
          this.saleBillModel.moneyCredit = dataRes.moneyCredit || 0;
          this.saleBillModel.moneyInstallment = dataRes.moneyInstallment || 0;

          this.paymentModel.storeId = dataRes.storeId;

          const billItems = (dataRes.billItems || []).filter(
            (item) =>
              item.billItemType === ITEM_TYPE.MAIN &&
              !item.flexibleComboId &&
              item.productType !== PRODUCT_TYPE.PRODUCT_COMBO,
          );
          this.originalItems = billItems.map((item) => ({
            productId: item.productId,
            productName: item.productName,
            productCode: item.productCode,
            barCode: item.barCode,
            imageUrl: item.imageUrl,
            imeiCode: item.imeiCode || '',
            productType: item.productType,
            quantity: item.quantity || 1,
            productPrice: item.productPrice || 0,
          }));
          this.mainModel.billItems = this.originalItems.map((item) =>
            this.buildExchangeRow(item),
          );

          setTimeout(() => {
            this.renderComponent = true;
          }, TIME_TRIGGER);
        })
        .catch((error) => {
          const message =
            error?.response?.data?.message ||
            `Không tải được hóa đơn ${this.mainModel.relatedBillNumber}!`;
          makeToastFaile(message);
          this.renderComponent = true;
        });
    },
    buildExchangeRow(item) {
      return {
        id: uuidv4(),
        productId: item.productId,
        productName: item.productName,
        productCode: item.productCode,
        barCode: item.barCode,
        imageUrl: item.imageUrl,
        imeiCode: item.imeiCode || '',
        productType: item.productType,
        oldQuantity: item.quantity || 1,
        productPrice: item.productPrice || 0,
        newProductId: null,
        newProductName: '',
        newProductCode: '',
        newBarCode: '',
        newImageUrl: '',
        newProductType: null,
        newSearchText: '',
        newImeiCode: '',
        newQuantity: item.quantity || 1,
        newProductPrice: item.productPrice || 0,
      };
    },
    buildEmptyRow() {
      return {
        id: uuidv4(),
        productId: null,
        productName: '',
        productCode: '',
        barCode: '',
        imageUrl: '',
        imeiCode: '',
        productType: null,
        oldQuantity: 1,
        productPrice: 0,
        newProductId: null,
        newProductName: '',
        newProductCode: '',
        newBarCode: '',
        newImageUrl: '',
        newProductType: null,
        newSearchText: '',
        newImeiCode: '',
        newQuantity: 1,
        newProductPrice: 0,
      };
    },
    onAddRow() {
      this.mainModel.billItems.push(this.buildEmptyRow());
    },
    onRemoveRow(rowId) {
      const index = this.mainModel.billItems.findIndex(
        (row) => row.id === rowId,
      );
      if (index > -1) {
        this.mainModel.billItems.splice(index, 1);
      }
    },
    buildPayload() {
      const billItems = this.mainModel.billItems.map((row) => {
        const isImei = this.isImeiRow(row);
        const newImeiCode = isImei ? (row.newImeiCode || '').trim() : null;
        const newQuantity = isImei ? 1 : Number(row.newQuantity) || 0;
        return {
          productId: row.productId,
          imeiCode: isImei ? row.imeiCode : null,
          quantity: Number(row.oldQuantity) || 1,
          productPrice: Number(unMaskPrice(row.productPrice)) || 0,
          billItemType: ITEM_TYPE.MAIN,
          newProductId: row.newProductId,
          newImeiCode,
          newProductName: row.newProductName,
          newQuantity,
          newProductPrice: Number(unMaskPrice(row.newProductPrice)) || 0,
          newProductType: row.newProductType || row.productType,
        };
      });
      return {
        relatedBillNumber: this.mainModel.relatedBillNumber,
        storeId: this.mainModel.storeId,
        newStoreId: this.mainModel.newStoreId,
        reasonId: this.mainModel.reasonId,
        customerName: this.mainModel.customerName,
        customerMobile: this.mainModel.customerMobile,
        customerAddress: this.mainModel.customerAddress,
        saleId: this.mainModel.saleId,
        cashierId: this.mainModel.cashierId,
        customerNote: this.mainModel.customerNote,
        saleNote: this.mainModel.saleNote,
        billItems,
        ...(this.requiresPayment
          ? {
              cash: this.paymentModel.cash,
              transfer: this.paymentModel.transfer,
              cashAmount: this.calcPaymentsAmount(this.paymentModel.cash),
              cashAccountId: this.paymentModel.cash[0]?.paymentRefId || null,
              transferAmount: this.calcPaymentsAmount(
                this.paymentModel.transfer,
              ),
              transferAccountID:
                this.paymentModel.transfer[0]?.paymentRefId || null,
              transferAccountCode:
                this.paymentModel.transfer[0]?.paymentCode || null,
              transferReferenceCode:
                this.paymentModel.transfer[0]?.paymentCode || null,
            }
          : {}),
      };
    },
    validate() {
      if (!this.mainModel.reasonId) {
        makeToastFaile('Vui lòng chọn lý do xuất đổi hợp lệ!');
        return false;
      }
      if (!this.mainModel.newStoreId) {
        makeToastFaile('Vui lòng chọn kho xuất hàng mới!');
        return false;
      }
      if (!this.isStoreAllowed(this.mainModel.newStoreId)) {
        makeToastFaile('Bạn không có quyền xuất hàng tại cửa hàng này!');
        return false;
      }
      if (this.isStaffReason && Math.abs(this.totalGiveValue - this.totalReturnValue) > 1) {
        makeToastFaile(
          'Xuất sai mã không được phát sinh chênh lệch giá sản phẩm!',
        );
        return false;
      }
      if (this.requiresPayment && this.paymentRemain > 0) {
        makeToastFaile(
          `Vui lòng thu đủ số tiền cần trả (${convertPrice(this.amountDue)})!`,
        );
        return false;
      }
      if (!this.mainModel.billItems.length) {
        makeToastFaile('Vui lòng chọn ít nhất một sản phẩm để xuất đổi!');
        return false;
      }
      for (const row of this.mainModel.billItems) {
        if (!row.productId) {
          makeToastFaile('Vui lòng chọn sản phẩm cũ cho tất cả cặp đổi!');
          return false;
        }
        if (!row.newProductId) {
          makeToastFaile(
            `Vui lòng chọn sản phẩm mới để đổi cho ${row.productName}!`,
          );
          return false;
        }
        if (this.isImeiRow(row) && !(row.newImeiCode || '').trim()) {
          makeToastFaile(
            `Vui lòng nhập IMEI cho sản phẩm mới ${row.newProductName}!`,
          );
          return false;
        }
      }
      return true;
    },
    onSubmit() {
      if (!this.validate()) {
        return;
      }
      this.onSubmiting = true;
      const payload = this.buildPayload();
      ApiService.post('v1/bills/exchange', payload)
        .then(({ data }) => {
          makeToastSuccess(data.message || 'Tạo hóa đơn xuất đổi thành công');
          this.$router.push({ name: 'list-bill' });
        })
        .catch((error) => {
          this.onSubmiting = false;
          const message = error?.response?.data?.message || 'Lỗi!';
          makeToastFaile(message);
        });
    },
  },
};
</script>

<style lang="scss" scoped>
.exchange-bill-info {
  .title-icon {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 44px;
    height: 44px;
    border-radius: 12px;
    background-color: #eef2ff;
    color: #3699ff;
    font-size: 1.1rem;
  }
  .mode-badge {
    border: 1px solid #e4e6ef;
    font-weight: 600;
    padding: 8px 12px;
    .status-dot {
      display: inline-block;
      width: 8px;
      height: 8px;
      border-radius: 50%;
      background-color: #1bc5bd;
      margin-right: 5px;
    }
  }

  .summary-row {
    margin-bottom: 4px;
  }
  .summary-card {
    display: flex;
    gap: 12px;
    align-items: flex-start;
    border: 1px solid #ebedf3;
    border-radius: 10px;
    padding: 14px;
    height: 100%;
    background-color: #fff;
  }
  .summary-icon {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 38px;
    height: 38px;
    border-radius: 9px;
    background-color: #f3f6f9;
    color: #7e8299;
    flex-shrink: 0;
  }
  .summary-label {
    font-size: 0.68rem;
    font-weight: 700;
    letter-spacing: 0.04em;
    color: #b5b5c3;
  }
  .summary-value {
    font-weight: 700;
    color: #181c32;
    font-size: 0.95rem;
  }
  .summary-sub {
    font-size: 0.75rem;
    color: #b5b5c3;
  }

  .block-card {
    border: 1px solid #ebedf3;
    border-radius: 10px;
    padding: 16px;
    background-color: #fff;

    ::v-deep .vue-treeselect__control {
      border-radius: 0.28rem;
      border: 1px solid #ced4da;
      height: calc(1.35em + 1.1rem + 2px);
    }
    ::v-deep .vue-treeselect__input-container {
      padding-top: 0;
    }
    ::v-deep .vue-treeselect__placeholder,
    ::v-deep .vue-treeselect__single-value {
      line-height: calc(1.35em + 1.1rem);
    }
  }
  .block-head {
    display: flex;
    align-items: flex-start;
  }
  .block-title {
    font-weight: 600;
    color: #181c32;
    font-size: 0.95rem;
  }

  .reason-tile {
    display: flex;
    flex-direction: column;
    border: 1px solid #e4e6ef;
    border-radius: 9px;
    padding: 12px;
    height: 100%;
    cursor: pointer;
    transition: all 0.15s ease;

    i {
      font-size: 1rem;
      color: #7e8299;
      margin-bottom: 6px;
    }
    .reason-name {
      font-weight: 600;
      color: #181c32;
      font-size: 0.85rem;
    }
    .reason-desc {
      font-size: 0.72rem;
      color: #b5b5c3;
    }
    &:hover {
      border-color: #b3d4fc;
    }
    &.active {
      border-color: #3699ff;
      background-color: #eef6ff;
      i,
      .reason-name {
        color: #3699ff;
      }
    }
  }

  .exchange-footer {
    position: sticky;
    bottom: 0;
    z-index: 95;
    display: flex;
    align-items: center;
    justify-content: space-between;
    flex-wrap: wrap;
    gap: 10px;
    margin: 8px -1.25rem -1.25rem;
    padding: 12px 24px;
    background-color: #fff;
    border-top: 1px solid #e4e6ef;
    border-radius: 0 0 0.42rem 0.42rem;
    box-shadow: 0 -2px 12px rgba(0, 0, 0, 0.05);
  }
  .footer-stats {
    display: flex;
    gap: 28px;
    flex-wrap: wrap;
  }
  .stat {
    display: flex;
    flex-direction: column;
    .stat-label {
      font-size: 0.66rem;
      font-weight: 700;
      letter-spacing: 0.03em;
      color: #b5b5c3;
    }
    .stat-value {
      font-weight: 700;
      color: #181c32;
      font-size: 0.95rem;
    }
  }
  .footer-actions {
    display: flex;
    align-items: center;
  }
}
</style>
